import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowRight, RefreshCw } from 'lucide-react';
import { GenieAvatar } from '@/components/GenieAvatar';
import { Button } from '@/components/ui/button';
import { GenieLoader } from '@/components/ui/GenieLoader';
import PageHeader from '@/components/common/PageHeader/PageHeader';
import { SearchInput } from '@/components/ui/search-input';
import { useUserAgents } from '@/hooks/agent/useAgents';
import { Agent } from '@/types/agent/agent';
import { DEFAULT_BRANDING } from '@/types/ui/branding';
import { resolveGenieAvatarUrl } from '@/utils/image/genieAvatars';
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

const ActivationGenieListPage = () => {
  const navigate = useNavigate();
  const { data: agents = [], isLoading, isError, refetch, isFetching } = useUserAgents(100);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredAgents = useMemo(() => {
    if (!searchTerm) return agents;
    const q = searchTerm.toLowerCase();
    return agents.filter((a: Agent) => a.name?.toLowerCase().includes(q));
  }, [agents, searchTerm]);

  const handleOpenPack = (agentId: string) => {
    // Opened from the global list — keep the list as the back destination.
    navigate(`/genie-hub/activation/genie/${agentId}`, {
      state: { from: { path: '/genie-hub/activation/all', label: 'all packs' } },
    });
  };

  const header = (
    <PageHeader
      title="All activation packs"
      genieIcon="qr-code"
      subtitle="Every Genie you can activate — QR codes, embed snippet, email signature, and print-ready assets."
    />
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F9FAFC]">
        <div className="container mx-auto px-4 py-6 md:py-8 max-w-[1920px]">
          {header}
          <GenieLoader size="block" loadingText="Loading your Genies…" className="py-16" />
        </div>
      </div>
    );
  }

  // A failed fetch must never be dressed up as "you have no Genies".
  if (isError) {
    return (
      <div className="min-h-screen bg-[#F9FAFC]">
        <div className="container mx-auto px-4 py-6 md:py-8 max-w-[1920px]">
          {header}
          <div className="mx-auto max-w-md rounded-2xl border border-gray-200 bg-white px-6 py-10 text-center">
            <AlertCircle className="mx-auto mb-3 h-8 w-8 text-red-600" />
            <h2 className="text-lg font-semibold text-gray-900">We couldn't load your Genies</h2>
            <p className="mt-1 text-sm text-gray-600">
              Nothing is wrong with your packs — we just couldn't reach them right now.
            </p>
            <Button
              variant="outline"
              className="mt-5"
              onClick={() => void refetch()}
              disabled={isFetching}
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
              Try again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFC]">
      <div className="container mx-auto px-4 py-6 md:py-8 max-w-[1920px]">
        {header}

        {agents.length > 0 ? (
          <>
            {/* Filter Bar */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <div className="text-lg font-semibold text-gray-800">
                Your Genies
                <span className="ml-2 text-sm text-gray-500">
                  ({filteredAgents.length} of {agents.length})
                </span>
              </div>
              <div className="p-[2px] rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
                <div className="rounded-[10px] bg-white">
                  <SearchInput
                    value={searchTerm}
                    onChange={setSearchTerm}
                    placeholder="Search Genies..."
                    className="w-64"
                    inputClassName="border-transparent focus-visible:ring-0"
                  />
                </div>
              </div>
            </div>

            {/* Genie Grid */}
            {filteredAgents.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
                {filteredAgents.map((agent: Agent) => (
                  <GenieActivationCard
                    key={agent.id}
                    agent={agent}
                    onOpen={() => handleOpenPack(agent.id)}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500 mb-4">No Genies match your search</p>
                <Button variant="outline" onClick={() => setSearchTerm('')}>
                  Clear search
                </Button>
              </div>
            )}
          </>
        ) : (
          /* Empty State */
          <div className="text-center py-16">
            <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-[#4E9CFF]/20 to-[#FF4DB8]/20 rounded-full flex items-center justify-center">
              <GenieIcon name="qr-code" size={48} className="w-12 h-12" />
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
              No Genies yet
            </h2>
            <p className="text-sm md:text-base text-gray-600 mb-6 md:mb-8 max-w-md mx-auto px-4">
              Create your first Genie to get a ready-to-ship activation pack with QR codes, embed snippet, email signature, and print-ready assets.
            </p>
            <Button
              onClick={() => navigate('/genie-hub/my-genies')}
              className="inline-flex items-center px-8 py-4 text-white rounded-xl font-semibold transition-all shadow-lg hover:opacity-90"
              style={{
                background: 'linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)',
              }}
            >
              Go to My Genies
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

interface GenieActivationCardProps {
  agent: Agent;
  onOpen: () => void;
}

const GenieActivationCard = ({ agent, onOpen }: GenieActivationCardProps) => {
  const brand = agent.branding?.primaryColor || DEFAULT_BRANDING.primaryColor;
  const hasCustomBranding = Boolean(
    agent.branding?.primaryColor && agent.branding.primaryColor !== DEFAULT_BRANDING.primaryColor,
  );

  return (
    <button
      onClick={onOpen}
      className="group relative bg-white rounded-2xl border border-gray-200 hover:border-transparent hover:shadow-xl transition-all p-5 md:p-6 text-left flex items-center gap-4"
    >
      <div className="flex-shrink-0 w-12 h-12 md:w-14 md:h-14">
        <GenieAvatar
          size={56}
          color={brand}
          customAvatarUrl={resolveGenieAvatarUrl(agent)}
          secondaryColor={agent.branding?.secondaryColor}
          gradientEnabled={agent.branding?.gradientEnabled}
          gradientAngle={agent.branding?.gradientAngle}
          avatarBackgroundOpacity={agent.branding?.avatarBackgroundOpacity}
          hasCustomBranding={hasCustomBranding}
        />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-gray-900 truncate">{agent.name}</h3>
        <p className="text-sm text-gray-500 mt-0.5">
          {agent.phone?.number ? 'Phone connected' : 'Activation pack ready'}
        </p>
      </div>
      <div className="flex-shrink-0 flex items-center text-[#4E9CFF] group-hover:text-[#FF4DB8] transition-colors">
        <span className="text-sm font-medium hidden md:inline mr-1">Open</span>
        <ArrowRight className="w-5 h-5" />
      </div>

      {/* Hover gradient border */}
      <div
        className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
        style={{
          background: 'linear-gradient(90deg, #4E9CFF, #FF4DB8)',
          padding: '2px',
          WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
        }}
      />
    </button>
  );
};

export default ActivationGenieListPage;
